import { HttpParams } from '@angular/common/http';
import {
  ChatDefinition,
  ChatMessage,
  ChatMessageQueryResult,
  EventGroupedFileActions,
  EventGroupedFileActionsResponse,
  FileAction,
  FileActionResponse,
  FileAnalysisStatus,
  FileDefinition,
  FileListResponse,
  Source,
  StateDefinition,
  TimelineEvent,
  TimelineQueryResponse,
} from 'src/api';
import { DemoCaseBundle } from './demo-case-bundle';
import {
  isListableFile,
  MessageMapperContext,
  toChatDefinition,
  toChatMessage,
  toFileAction,
  toFileActionSummary,
  toFileActionListItem,
  toFileDefinition,
  toFileMeta,
  toSource,
  toStateDefinition,
} from './demo-api.mappers';
import { RawBranch, RawFile, RawMessage, RawState } from './demo-export.types';

/**
 * Endpoint behaviour for demo cases: the filtering, sorting, paging and derivation that the
 * API does in SQL, done here over the exported rows.
 *
 * Each handler names the route it stands in for. Anything the export cannot know (whether a
 * run is in progress, which messages are streaming) resolves to the "finished, idle" answer,
 * which is what a snapshot of a case actually is.
 */

export class DemoRequestNotFound extends Error {}

export interface DemoRequestContext {
  bundle: DemoCaseBundle;
  /** Mirrors `is_admin()`, which decides whether AI reasoning blocks stay in message content. */
  isAdminViewer: boolean;
}

const numberParam = (params: HttpParams, name: string): number | undefined => {
  const raw = params.get(name);
  if (raw === null || raw === '') return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const boolParam = (params: HttpParams, name: string): boolean =>
  params.get(name) === 'true' || params.get(name) === '1';

/** `_is_duplicate_file_predicate`: the new status, or the legacy `version_info` marker. */
const isDuplicateFile = (file: RawFile): boolean =>
  file.analysis_status === 'duplicate' || file.version_info?.['duplicate'] != null;

/**
 * The `status_in` buckets of `apply_file_filters`, as the API defines them — including
 * `NOT SUPPORTED` keying off `analysis_status IS NOT NULL`: a demo case has to answer the
 * document list's filters the way the real case would.
 */
const STATUS_IN_PREDICATES: Record<string, (file: RawFile) => boolean> = {
  DUPLICATE: isDuplicateFile,
  ERROR: (file) => file.analysis_status === 'error',
  DRAFT: (file) => file.draft_status === 'pending' || file.draft_status === 'finalized',
  DONE: (file) => file.analysis_status === 'done' && file.draft_status !== 'pending',
  'IN PROGRESS': (file) =>
    ['in_progress', 'reading', 'analyzing'].includes(file.analysis_status ?? ''),
  IMPORTING: (file) => file.analysis_status === 'importing',
  SYNCING: (file) => file.analysis_status === 'syncing',
  PENDING: (file) => file.analysis_status === 'pending',
  'NOT SUPPORTED': (file) =>
    !isDuplicateFile(file) &&
    (file.analysis_status == null ||
      file.analysis_status === 'not_supported' ||
      !['error', 'done', 'pending', 'in_progress', 'reading', 'analyzing', 'importing', 'syncing'].includes(file.analysis_status)),
};

/**
 * `status_in`: an OR over the requested buckets. Unrecognised values match everything, mirroring
 * the endpoint's `if len(statements) > 0` guard, which leaves the query unfiltered rather than
 * returning nothing.
 */
function matchesStatusIn(file: RawFile, statuses: readonly string[]): boolean {
  const known = statuses.filter((status) =>
    Object.prototype.hasOwnProperty.call(STATUS_IN_PREDICATES, status),
  );
  if (known.length === 0) return true;
  return known.some((status) => STATUS_IN_PREDICATES[status](file));
}

// ---------------------------------------------------------------------------- GET /files

/** `Files.get` + `apply_file_filters` in `routes/files.py`. */
export function listFiles(bundle: DemoCaseBundle, params: HttpParams): FileListResponse {
  const parentFileId = params.get('parent_file_id');
  const fileType = params.get('file_type');
  const filenameContains = params.get('filename_contains')?.toLowerCase();
  const mimeTypes = params.getAll('mime_type_in') ?? [];
  const statuses = params.getAll('status_in') ?? [];
  const sortBy = params.get('sort_by');
  const sortDirection = params.get('sort_direction') === 'desc' ? 'desc' : 'asc';
  const offset = numberParam(params, 'offset');
  const limit = numberParam(params, 'limit');

  const uploadedAfter = params.get('uploaded_at_after');
  const uploadedBefore = params.get('uploaded_at_before');
  const modifiedAfter = params.get('modified_at_after');
  const modifiedBefore = params.get('modified_at_before');
  const sizeGreaterThan = numberParam(params, 'size_greater_than');
  const sizeLessThan = numberParam(params, 'size_less_than');

  let matches = bundle.files.filter(isListableFile);

  if (parentFileId !== null) {
    // An empty parent_file_id means "at the case root".
    matches =
      parentFileId === ''
        ? matches.filter((file) => !file.parent_file)
        : matches.filter((file) => file.parent_file === parentFileId);
  }
  if (fileType !== null) {
    matches = matches.filter((file) => file.is_folder === (fileType === 'folder'));
  }
  if (filenameContains) {
    matches = matches.filter((file) => file.filename.toLowerCase().includes(filenameContains));
  }
  if (mimeTypes.length > 0) {
    matches = matches.filter((file) => mimeTypes.includes(file.mime_type));
  }
  if (statuses.length > 0) {
    matches = matches.filter((file) => matchesStatusIn(file, statuses));
  }
  matches = filterByDate(matches, 'uploaded_at', uploadedAfter, uploadedBefore);
  matches = filterByDate(matches, 'modified_at', modifiedAfter, modifiedBefore);
  if (sizeGreaterThan !== undefined) {
    matches = matches.filter((file) => file.size > sizeGreaterThan);
  }
  if (sizeLessThan !== undefined) {
    matches = matches.filter((file) => file.size < sizeLessThan);
  }

  if (sortBy === 'uploaded_at' || sortBy === 'name') {
    const direction = sortDirection === 'desc' ? -1 : 1;
    matches = [...matches].sort((a, b) => {
      const comparison =
        sortBy === 'name'
          ? a.filename.localeCompare(b.filename)
          : Date.parse(a.uploaded_at) - Date.parse(b.uploaded_at);
      return comparison * direction;
    });
  }

  const total = matches.length;
  const page = matches.slice(offset ?? 0, limit === undefined ? undefined : (offset ?? 0) + limit);

  return {
    files: page.map((file) => toFileDefinition(bundle, file)),
    total_file_count: total,
    offset: offset ?? 0,
    limit: limit ?? total,
  };
}

function filterByDate(
  files: RawFile[],
  field: 'uploaded_at' | 'modified_at',
  after: string | null,
  before: string | null,
): RawFile[] {
  let result = files;
  if (after) {
    const bound = Date.parse(after);
    result = result.filter((file) => Date.parse(file[field]) > bound);
  }
  if (before) {
    const bound = Date.parse(before);
    result = result.filter((file) => Date.parse(file[field]) < bound);
  }
  return result;
}

/** `FileTrashbin.get`. Exports never contain trashed files, so this is always empty. */
export function getFilesInTrashbin(params: HttpParams): FileListResponse {
  return {
    files: [],
    total_file_count: 0,
    offset: numberParam(params, 'offset') ?? 0,
    limit: numberParam(params, 'limit') ?? 0,
  };
}

export async function getFileMeta(bundle: DemoCaseBundle, fileId: string) {
  const file = bundle.filesById.get(fileId);
  if (!file) throw new DemoRequestNotFound(`File ${fileId} is not part of demo case ${bundle.caseId}`);
  return toFileMeta(bundle, file, await bundle.fileMeta(fileId));
}

export function getFileById(bundle: DemoCaseBundle, fileId: string): FileDefinition {
  const file = bundle.filesById.get(fileId);
  if (!file) throw new DemoRequestNotFound(`File ${fileId} is not part of demo case ${bundle.caseId}`);
  return toFileDefinition(bundle, file);
}

/** `FileVersions.get` — the file's action history, newest first. */
export function getFileVersions(bundle: DemoCaseBundle, fileId: string): FileActionResponse {
  const file = bundle.filesById.get(fileId);
  if (!file) throw new DemoRequestNotFound(`File ${fileId} is not part of demo case ${bundle.caseId}`);
  return {
    actions: [...file.actions]
      .sort((a, b) => Date.parse(b.action_at) - Date.parse(a.action_at))
      .map(toFileActionListItem),
  };
}

/** `analysis_status_response_from_counts` in `helpers/file_analysis_status.py`. */
export function getAnalysisStatus(bundle: DemoCaseBundle, params: HttpParams): FileAnalysisStatus {
  const counts: Record<string, number> = {
    importing: 0,
    syncing: 0,
    pending: 0,
    in_progress: 0,
    reading: 0,
    analyzing: 0,
    done: 0,
    duplicate: 0,
    error: 0,
    not_supported: 0,
    input_required: 0,
  };

  for (const file of bundle.files) {
    if (file.is_folder || !file.analysis_status || !isListableFile(file)) continue;
    const status = isDuplicateFile(file) ? 'duplicate' : file.analysis_status;
    if (status in counts) counts[status] += 1;
  }

  const analyzing = counts['importing'] + counts['syncing'] + counts['pending'] + counts['in_progress'] + counts['reading'] + counts['analyzing'];
  const includeErrorFiles = boolParam(params, 'include_error_files');
  const errorFiles = includeErrorFiles
    ? bundle.files
        .filter((file) => file.analysis_status === 'error' && isListableFile(file))
        .map((file) => toFileDefinition(bundle, file))
    : undefined;

  return {
    analyzing_count: analyzing,
    importing_count: counts['importing'],
    syncing_count: counts['syncing'],
    pending_count: counts['pending'],
    in_progress_count: counts['in_progress'],
    analyzing_status_count: counts['analyzing'],
    reading_count: counts['reading'],
    duplicate_count: counts['duplicate'],
    error_count: counts['error'],
    done_count: counts['done'],
    input_required_count: counts['input_required'],
    // Nothing is in flight in a snapshot, so there is no case to report as still processing.
    case_ids: [],
    is_case_done_notification_read: true,
    error_files: errorFiles,
    error_files_truncated: false,
    error_files_total_count: errorFiles?.length ?? 0,
  };
}

// ---------------------------------------------------------------------------- chats & state

/** `CaseChats.get` — ordering mirrors the generic query options the frontend passes. */
export function listChatsInCase(bundle: DemoCaseBundle, params: HttpParams): ChatDefinition[] {
  const sortBy = params.get('sort_by') ?? 'created_at';
  const direction = params.get('sort_direction') === 'asc' ? 1 : -1;
  const offset = numberParam(params, 'offset') ?? 0;
  const limit = numberParam(params, 'limit');

  const sorted = [...bundle.chats].sort((a, b) => {
    const comparison =
      sortBy === 'name'
        ? (a.name ?? '').localeCompare(b.name ?? '')
        : Date.parse(a[sortBy === 'last_opened_at' ? 'last_opened_at' : 'created_at'] ?? a.created_at) -
          Date.parse(b[sortBy === 'last_opened_at' ? 'last_opened_at' : 'created_at'] ?? b.created_at);
    return comparison * direction;
  });

  return sorted
    .slice(offset, limit === undefined ? undefined : offset + limit)
    .map(toChatDefinition);
}

export function getChatById(bundle: DemoCaseBundle, chatId: string): ChatDefinition {
  const chat = bundle.chatsById.get(chatId);
  if (!chat) throw new DemoRequestNotFound(`Chat ${chatId} is not part of demo case ${bundle.caseId}`);
  return toChatDefinition(chat);
}

export async function getStateById(
  bundle: DemoCaseBundle,
  stateId: string,
): Promise<StateDefinition> {
  return toStateDefinition(bundle, await bundle.state(stateId));
}

/**
 * `_branch_lineage_recursive_cte`: the chain from `branchId` up to the root branch, each
 * ancestor tagged with the checkpoint its child forked off at.
 */
function branchLineage(
  state: RawState,
  branchId: string,
): { branch: RawBranch; cutoffCheckpointId: string | null }[] {
  const branchesById = new Map(state.branches.map((branch) => [branch.id, branch]));
  const checkpointToBranch = new Map<string, string>();
  for (const branch of state.branches) {
    for (const checkpoint of branch.checkpoints) {
      checkpointToBranch.set(checkpoint.id, branch.id);
    }
  }

  const lineage: { branch: RawBranch; cutoffCheckpointId: string | null }[] = [];
  let current = branchesById.get(branchId);
  let cutoff: string | null = null;
  const seen = new Set<string>();

  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    lineage.push({ branch: current, cutoffCheckpointId: cutoff });

    const forkedFrom = current.branched_off_from_checkpoint;
    if (!forkedFrom) break;
    const parentBranchId = checkpointToBranch.get(forkedFrom);
    current = parentBranchId ? branchesById.get(parentBranchId) : undefined;
    cutoff = forkedFrom;
  }

  return lineage;
}

/**
 * `_visible_checkpoint_ids_recursive_cte`: every checkpoint of the current branch, plus each
 * ancestor's checkpoints from its fork point backwards. Messages created after a fork belong
 * to the sibling branch and must stay hidden.
 */
function visibleCheckpointIds(state: RawState, branchId: string): Set<string> {
  const checkpointsById = new Map(
    state.branches.flatMap((branch) => branch.checkpoints.map((cp) => [cp.id, cp] as const)),
  );
  const visible = new Set<string>();

  const walkBack = (startId: string | null) => {
    let cursor = startId;
    while (cursor && !visible.has(cursor)) {
      visible.add(cursor);
      cursor = checkpointsById.get(cursor)?.previous_checkpoint ?? null;
    }
  };

  for (const { branch, cutoffCheckpointId } of branchLineage(state, branchId)) {
    if (cutoffCheckpointId === null) {
      for (const checkpoint of branch.checkpoints) visible.add(checkpoint.id);
    } else {
      walkBack(cutoffCheckpointId);
    }
  }

  return visible;
}

function triggerEventsByCheckpoint(state: RawState): Map<string, string | null> {
  return new Map(
    state.branches.flatMap((branch) =>
      branch.checkpoints.map(
        (checkpoint) => [checkpoint.id, checkpoint.active_workflow_trigger_event_id] as const,
      ),
    ),
  );
}

/** `_base_messages_query_for_branch`: lineage- and checkpoint-visible messages, newest first. */
function visibleMessages(
  state: RawState,
  branchId: string,
  includeSystemMessages: boolean,
): RawMessage[] {
  const lineage = branchLineage(state, branchId);
  const visibleCheckpoints = visibleCheckpointIds(state, branchId);

  const messages = lineage
    .flatMap(({ branch }) => branch.messages)
    .filter(
      (message) =>
        !!message.creation_checkpoint_id && visibleCheckpoints.has(message.creation_checkpoint_id),
    )
    .filter((message) => includeSystemMessages || (message.role !== 'system' && !message.internal));

  return messages.sort((a, b) => Date.parse(b.sent_at) - Date.parse(a.sent_at));
}

/** `ChatMessages.get` — offset counts back from the newest message. */
export async function queryChatMessages(
  context: DemoRequestContext,
  stateId: string,
  branchId: string,
  params: HttpParams,
): Promise<ChatMessageQueryResult> {
  const { bundle } = context;
  const state = await bundle.state(stateId);
  const offset = numberParam(params, 'offset') ?? 0;
  const limit = numberParam(params, 'limit') ?? 25;
  const includeSystem = boolParam(params, 'include_system_messages');

  const all = visibleMessages(state, branchId, includeSystem);
  const page = all.slice(offset, offset + limit);
  const childMessages = await resolveChildMessages(context, page);
  const triggerEventByCheckpoint = triggerEventsByCheckpoint(state);

  return {
    total: all.length,
    messages: page.map((message) =>
      toChatMessage(message, {
        bundle,
        stateId,
        isAdminViewer: context.isAdminViewer,
        childMessages,
        triggerEventByCheckpoint,
      }),
    ),
    // A snapshot has nothing mid-stream.
    streaming_ids: [],
  };
}

/**
 * A message's `child_messages` are the primary-branch messages of the state that was spawned
 * from it, in send order. Only the sub-states belonging to this page are fetched.
 */
async function resolveChildMessages(
  context: DemoRequestContext,
  messages: readonly RawMessage[],
): Promise<Map<string, ChatMessage[]>> {
  const { bundle } = context;
  const parentIds = messages
    .map((message) => message.id)
    .filter((id) => bundle.stateIdsByParentMessageId.has(id));
  if (parentIds.length === 0) return new Map();

  const childStates = await bundle.childStates(parentIds);
  const byParent = new Map<string, ChatMessage[]>();

  for (const childState of childStates) {
    if (!childState.parent_message_id) continue;
    const primaryBranch =
      childState.branches.find((branch) => branch.id === childState.primary_branch) ??
      childState.branches.at(-1);
    if (!primaryBranch) continue;

    const triggerEventByCheckpoint = triggerEventsByCheckpoint(childState);
    const mapped = [...primaryBranch.messages]
      .sort((a, b) => Date.parse(a.sent_at) - Date.parse(b.sent_at))
      .map((message) =>
        toChatMessage(message, {
          bundle,
          stateId: childState.id,
          isAdminViewer: context.isAdminViewer,
          triggerEventByCheckpoint,
        } satisfies MessageMapperContext),
      );

    const existing = byParent.get(childState.parent_message_id);
    byParent.set(childState.parent_message_id, existing ? [...existing, ...mapped] : mapped);
  }

  return byParent;
}

export async function getMessageById(
  context: DemoRequestContext,
  stateId: string,
  messageId: string,
): Promise<ChatMessage> {
  const state = await context.bundle.state(stateId);
  const raw = state.branches
    .flatMap((branch) => branch.messages)
    .find((message) => message.id === messageId);
  if (!raw) {
    throw new DemoRequestNotFound(`Message ${messageId} not found in state ${stateId}`);
  }

  const childMessages = await resolveChildMessages(context, [raw]);
  return toChatMessage(raw, {
    bundle: context.bundle,
    stateId,
    isAdminViewer: context.isAdminViewer,
    childMessages,
    triggerEventByCheckpoint: triggerEventsByCheckpoint(state),
  });
}

/** `MessageFileActions.get` — the file actions a message produced. */
export function getFileActionsForMessage(
  bundle: DemoCaseBundle,
  messageId: string,
): FileActionResponse {
  return {
    actions: bundle.files
      .flatMap((file) => file.actions)
      .filter((action) => action.message_id === messageId)
      .sort((a, b) => Date.parse(a.action_at) - Date.parse(b.action_at))
      .map(toFileActionListItem),
  };
}

/**
 * `FileActionsByEvent.get`: actions grouped by the `trigger_event_id` their message carries.
 * The events table is not exported, so groups keep the endpoint's own fallback of an unknown
 * type and no timestamp.
 */
export async function getFileActionsGroupedByEvent(
  bundle: DemoCaseBundle,
  stateId: string,
  branchId: string,
): Promise<EventGroupedFileActionsResponse> {
  // Descend the state tree the way the endpoint does: a sub-agent books its file actions on its own
  // state, so a run's actions are spread over the states spawned from this one's messages. Only the
  // requested state is scoped to a branch, as the endpoint does; sub-states are taken whole.
  const triggerEventByMessageId = new Map<string, string | null>();
  let frontier = bundle.stateIndexById.has(stateId) ? [stateId] : [];
  const seen = new Set<string>(frontier);

  while (frontier.length > 0) {
    const states = await Promise.all(frontier.map((id) => bundle.state(id)));
    const next: string[] = [];
    for (const state of states) {
      const visibleCheckpoints =
        state.id === stateId ? visibleCheckpointIds(state, branchId) : null;
      for (const branch of state.branches) {
        for (const message of branch.messages) {
          if (
            visibleCheckpoints !== null &&
            (!message.creation_checkpoint_id ||
              !visibleCheckpoints.has(message.creation_checkpoint_id))
          ) {
            continue;
          }
          const additional = message.content?.['additional_kwargs'] as
            | Record<string, unknown>
            | undefined;
          const triggerEventId = additional?.['trigger_event_id'];
          triggerEventByMessageId.set(
            message.id,
            typeof triggerEventId === 'string' ? triggerEventId : null,
          );
          for (const childId of bundle.stateIdsByParentMessageId.get(message.id) ?? []) {
            if (seen.has(childId) || !bundle.stateIndexById.has(childId)) continue;
            seen.add(childId);
            next.push(childId);
          }
        }
      }
    }
    frontier = next;
  }

  const groups = new Map<string, EventGroupedFileActions>();
  const actions = bundle.files
    .flatMap((file) => file.actions)
    .filter((action) => !!action.message_id && triggerEventByMessageId.has(action.message_id))
    .sort((a, b) => Date.parse(b.action_at) - Date.parse(a.action_at));

  for (const action of actions) {
    const eventId = triggerEventByMessageId.get(action.message_id as string) ?? null;
    const key = eventId ?? 'no-event';
    const group = groups.get(key);
    if (group) {
      group.file_actions.push(toFileActionSummary(action));
    } else {
      groups.set(key, {
        event_id: eventId ?? undefined,
        event_type: 'unknown',
        event_timestamp: undefined,
        file_actions: [toFileActionSummary(action)],
      });
    }
  }

  return { grouped_file_actions: [...groups.values()] };
}

/** `FileActionDiff.get`: one action with its whole `action_metadata`. */
export function getFileActionDiff(bundle: DemoCaseBundle, fileActionId: string): FileAction {
  const action = bundle.files.flatMap((file) => file.actions).find((candidate) => candidate.id === fileActionId);
  if (!action) throw new DemoRequestNotFound(`File action ${fileActionId} is not part of demo case ${bundle.caseId}`);
  return toFileAction(action);
}

// ---------------------------------------------------------------------------- sources

/** `CaseSourceById.get`, including its `<base>_<n>` suffix fallback. */
export function getSourceById(bundle: DemoCaseBundle, sourceId: string): Source {
  const direct = bundle.sourcesById.get(sourceId);
  if (direct) return toSource(bundle, direct);

  const base = /^(.+)_\d+(:\d+)*$/.exec(sourceId.trim())?.[1];
  const fallback = base ? bundle.sourcesById.get(base) : undefined;
  if (fallback) return toSource(bundle, fallback);

  throw new DemoRequestNotFound(`Source ${sourceId} is not part of demo case ${bundle.caseId}`);
}

// ---------------------------------------------------------------------------- timeline

const TIMELINE_CLAIM_TYPES = new Set(['deadline', 'deadlineInfo', 'importantDate']);
const TIMELINE_ENTITY_TYPES = new Set([
  'date',
  'dateRange',
  'invoiceItem',
  'idNumber',
  'importantDate',
  'deadlineInfo',
]);

function parsedDate(meta: Record<string, unknown> | null | undefined): string | null {
  const parsed = meta?.['parsed'] as Record<string, unknown> | undefined;
  const date = parsed?.['date'];
  if (typeof date !== 'string' || Number.isNaN(Date.parse(date))) return null;
  return date;
}

/**
 * `CaseTimeline.get`: dated claims and entities, each nearest-to-now first, paged
 * independently and then merged in chronological order.
 */
export function listTimelineEventsInCase(
  bundle: DemoCaseBundle,
  params: HttpParams,
): TimelineQueryResponse {
  const offset = numberParam(params, 'offset') ?? 0;
  const limit = numberParam(params, 'limit');
  const now = Date.now();
  const byDistanceToNow = (a: { date: string }, b: { date: string }) =>
    Math.abs(Date.parse(a.date) - now) - Math.abs(Date.parse(b.date) - now);

  const snippetPageById = new Map(
    bundle.knowledge.snippets.map((snippet) => [snippet.text_snippet, snippet] as const),
  );

  const sourceOf = (join: { file_id: string; source_meta: Record<string, unknown> | null }) => {
    const file = bundle.filesById.get(join.file_id);
    const snippetIds = (join.source_meta?.['sourceSnippets'] as unknown[] | undefined) ?? [];
    const snippetId = snippetIds[0];
    const snippet = typeof snippetId === 'number' ? snippetPageById.get(snippetId) : undefined;
    return {
      source_document: file ? toFileDefinition(bundle, file) : undefined,
      source_snippet: snippet
        ? {
            id: String(snippet.text_snippet),
            page_start: snippet.page_start ?? undefined,
            page_end: snippet.page_end ?? undefined,
            page_position: snippet.page_position ?? undefined,
          }
        : undefined,
    };
  };

  const claimEvents = bundle.knowledge.claims
    .filter((claim) => TIMELINE_CLAIM_TYPES.has(claim.claim_type_name ?? ''))
    .flatMap((claim) => {
      const date = parsedDate(claim.meta);
      const join = bundle.knowledge.claim_files.find((row) => row.claim_id === claim.id);
      if (!date || !join) return [];
      return [
        {
          id: claim.id,
          date,
          description: claim.text,
          type: claim.claim_type_name ?? 'unknown',
          ...sourceOf(join),
        } satisfies TimelineEvent,
      ];
    })
    .sort(byDistanceToNow);

  const entityEvents = bundle.knowledge.entities
    .filter((entity) => TIMELINE_ENTITY_TYPES.has(entity.entity_type_name ?? ''))
    .flatMap((entity) => {
      const date = parsedDate(entity.meta);
      const join = bundle.knowledge.entity_files.find((row) => row.entity_id === entity.id);
      if (!date || !join) return [];
      return [
        {
          id: entity.id,
          date,
          description: (entity.claims ?? []).map((claim) => claim.text).join(' '),
          type: entity.entity_type_name ?? 'unknown',
          ...sourceOf(join),
        } satisfies TimelineEvent,
      ];
    })
    .sort(byDistanceToNow);

  const page = <T>(items: T[]) =>
    items.slice(offset, limit === undefined ? undefined : offset + limit);

  const events = [...page(claimEvents), ...page(entityEvents)].sort(
    (a, b) => Date.parse(a.date) - Date.parse(b.date),
  );

  return { events, total: claimEvents.length + entityEvents.length };
}
